'use client';

import { useState } from 'react';
import { useClientTranslation, useClientLocale } from '@bts-web/utils-lokalise';
import {
  intlFiatValueFormatting,
  MaximumFractionDigits,
} from '@bts-web/utils-formatting';
import {
  ButtonBase,
  NavigationBar,
  AmountInputWrapper,
  AmountInput,
  getAmountInputPlaceHolder,
} from '../../../../common';
import { QuickSelectButtons } from '../../../CreateTrade/MainTradeForm/subcomponents/QuickSelectButtons/QuickSelectButtons';
import { createPercentageQuickSelectItem } from '../../../CreateTrade/utils/getTradeQuickSelectButtons';
import { PERCENTAGE_QUICK_SELECT_VALUES } from '../../../CreateTrade/constants';
import { getAppConfig } from '@bts-web/core-app-config';
import { FiatSymbolIcon } from '../../Reusable/FiatSymbolIcon/FiatSymbolIcon';
import { CloseDrawerButton } from '../../../../common/components/Drawer/CloseDrawerButton';
import { MainTradeFormErrorBox } from '../../../CreateTrade/MainTradeFormErrorBox/MainTradeFormErrorBox';
import { validateAmountInput } from '../../../../common/utils/amountInputs/validateAmountInput';
import { getWithdrawalAmountTranslations } from './getWithdrawalAmountTranslations';
import { useFiatTransactionFee } from '../../Wallet/useFiatTransactionFee.use-client';
import { summaryWrapperStyles } from '../WithdrawalSummary/withdrawalSummaryContentStyles';
import {
  scrollableBodyStyles,
  bottomSectionStyles,
  inputSectionStyles,
  borderedSectionStyles,
  assetRowStyles,
  assetNameStyles,
  helperRowStyles,
  helperTextStyles,
  availableRowStyles,
  availableAssetInfoStyles,
  availableLabelStyles,
  availableNameStyles,
  availableTrailingStyles,
  disclaimerSectionStyles,
  disclaimerTitleStyles,
} from './withdrawalAmountStyles';
import { WithdrawalFeesBreakdown } from '../Reusable/WithdrawalFeesBreakdown';

export interface WithdrawalAmountContentProps {
  walletAmount: string;
  withdrawalBalance: string;
  unconfirmedBalance: string;
  minimumAmount: string;
  transferFees?: string;
  initialAmount?: string;
  onConfirm: (amount: string) => void;
}

export const WithdrawalAmountContent = ({
  walletAmount,
  withdrawalBalance,
  unconfirmedBalance,
  initialAmount,
  minimumAmount,
  onConfirm,
}: WithdrawalAmountContentProps) => {
  const { t } = useClientTranslation();

  const { currency } = getAppConfig();

  const translations = getWithdrawalAmountTranslations(t, currency);

  const locale = useClientLocale();

  const [amount, setAmount] = useState(initialAmount || '');

  const normalizedAmount = amount.replace(',', '.');

  const parsedAmount = Number(normalizedAmount);

  const parsedMin = Number(minimumAmount);

  const parsedAvailable = Number(withdrawalBalance);

  const isWithdrawalDisabled = parsedAvailable === 0;

  const { fee, isLoading: isFeeLoading } = useFiatTransactionFee(
    amount,
    'WITHDRAWAL',
    minimumAmount,
  );

  const isAmountValid =
    parsedAmount > 0 &&
    parsedAmount >= parsedMin &&
    parsedAmount <= parsedAvailable;

  const maximumFiatAmount = {
    FIAT: { value: withdrawalBalance, precision: MaximumFractionDigits.FIAT },
    ASSET: { value: '0', precision: MaximumFractionDigits.ASSET },
  };

  const percentageLabels = [
    translations.percentage25,
    translations.percentage50,
    translations.percentage75,
    translations.percentage100,
  ];

  const quickSelectItems = PERCENTAGE_QUICK_SELECT_VALUES.map((pct, i) =>
    createPercentageQuickSelectItem(
      pct,
      maximumFiatAmount,
      percentageLabels[i],
    ),
  );

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (validateAmountInput(value, 'FIAT')) {
      setAmount(value);
    }
  };

  const formatFiat = (value: number) =>
    intlFiatValueFormatting(String(value), { locale, currency });

  return (
    <div
      data-testid="withdrawal-amount-content"
      className={summaryWrapperStyles}
    >
      <NavigationBar
        title={translations.navTitle}
        NavigationRightSlot={<CloseDrawerButton />}
      />

      <div className={scrollableBodyStyles}>
        <div className={inputSectionStyles}>
          <span className={assetRowStyles}>
            <FiatSymbolIcon iconSize={24} containerSize={24} />

            <span className={assetNameStyles}>{currency}</span>
          </span>

          <AmountInputWrapper
            inputContextData={{
              displayType: 'FIAT',
              value: amount,
              currency,
              symbol: currency,
              placeholder: getAmountInputPlaceHolder({
                locale,
                currency,
                displayType: 'FIAT',
                symbol: currency,
              }),
            }}
          >
            {({ inputRef, modifiedBlur }) => (
              <AmountInput
                ref={inputRef}
                placeholder={getAmountInputPlaceHolder({
                  locale,
                  currency,
                  displayType: 'FIAT',
                  symbol: currency,
                })}
                value={amount}
                onChange={handleAmountChange}
                onBlur={modifiedBlur}
                disabled={isWithdrawalDisabled}
              />
            )}
          </AmountInputWrapper>

          <QuickSelectButtons
            items={quickSelectItems}
            onQuickSelectButtonClick={(selected) => {
              if (selected.FIAT) setAmount(selected.FIAT.value);
            }}
            disabled={isWithdrawalDisabled}
            displayType="FIAT"
            inputValue={amount}
          />
        </div>

        <div className={borderedSectionStyles}>
          <div className={helperRowStyles}>
            {amount !== '' &&
            (parsedAmount > parsedAvailable || parsedAmount < parsedMin) ? (
              <MainTradeFormErrorBox
                errors={{
                  tooHigh: parsedAmount > parsedAvailable,
                  tooLow: parsedAmount < parsedMin,
                }}
                errorTooHigh={translations.errorTooHigh}
                errorTooLow={translations.errorTooLow}
              />
            ) : (
              <p className={helperTextStyles}>
                {`${translations.minimum}: ${formatFiat(parsedMin)}`}
              </p>
            )}
          </div>

          <div className={availableRowStyles}>
            <FiatSymbolIcon />

            <div className={availableAssetInfoStyles}>
              <span className={availableLabelStyles}>{translations.from}</span>

              <span className={availableNameStyles}>{translations.wallet}</span>
            </div>

            <div className={availableTrailingStyles}>
              <span className={availableLabelStyles}>
                {translations.available}
              </span>

              <span className={availableNameStyles}>
                {formatFiat(parsedAvailable)}
              </span>
            </div>
          </div>
        </div>
        {Number(withdrawalBalance) !== Number(walletAmount) && (
          <div className={disclaimerSectionStyles}>
            <span className={disclaimerTitleStyles}>
              {translations.availableAmountTitle}
            </span>
            <span>
              {t(translations.availableAmountDescription, {
                withdrawalBalance: formatFiat(Number(withdrawalBalance)),
                walletAmount: formatFiat(Number(walletAmount)),
                unconfirmedBalance: formatFiat(Number(unconfirmedBalance)),
              })}
            </span>
          </div>
        )}
      </div>

      <div className={bottomSectionStyles}>
        <WithdrawalFeesBreakdown
          amount={normalizedAmount}
          fee={fee}
          isLoading={isFeeLoading}
        />

        <ButtonBase
          data-testid="withdrawal-amount-confirm-button"
          visual="accent"
          size="large"
          onClick={() => onConfirm(normalizedAmount)}
          disabled={!isAmountValid || isFeeLoading}
          fullWidth
          noHover
        >
          {translations.confirm}
        </ButtonBase>
      </div>
    </div>
  );
};
